// source: template-project@7007a338a apps/backoffice/src/components/ui/shadcn/badge.tsx
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import type { BadgeIntent } from "@/design-system/tokens"

/**
 * Trafiflow UI v3 badge: five intents, each a fill + label pair that clears
 * 4.5:1 in both modes (the badge-* tokens). 12px / 500 on a 16px line, 3px 10px
 * padding, no visible edge; the optional dot takes the label colour. A badge
 * is a state word, never a control: it may be a pill (the lint allows
 * `rounded-full` off controls).
 *
 * The pre-v3 `variant`s stay for the ~275 call sites and render as they did
 * under the paper theme: `default` and `destructive` are solid fills,
 * `secondary` the 5% ink wash, `outline` label-only (the theme had already
 * removed its edge). New code says what it means with `intent`.
 */
export const BADGE_INTENT_CLASSES: Record<BadgeIntent, string> = {
  success: "bg-ds-badge-success-bg text-ds-badge-success-text",
  warning: "bg-ds-badge-warning-bg text-ds-badge-warning-text",
  error: "bg-ds-badge-error-bg text-ds-badge-error-text",
  info: "bg-ds-badge-info-bg text-ds-badge-info-text",
  neutral: "bg-ds-badge-neutral-bg text-ds-badge-neutral-text",
}

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-transparent px-2.5 py-[3px] text-label transition-colors",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
      intent: BADGE_INTENT_CLASSES,
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** A 6px dot in the label colour before the text (status badges). */
  dot?: boolean
}

function Badge({ className, variant, intent, dot = false, children, ...props }: BadgeProps) {
  // An intent says everything about the colour; the legacy variant is ignored then.
  const resolvedVariant = intent ? null : variant
  return (
    <div
      data-slot="badge"
      data-variant={intent ?? resolvedVariant ?? "default"}
      className={cn(badgeVariants({ variant: resolvedVariant, intent }), className)}
      {...props}
    >
      {dot ? <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" /> : null}
      {children}
    </div>
  )
}

export { Badge, badgeVariants }
